import { motion, useReducedMotion } from 'motion/react';
import { CountUp } from './CountUp';
import { Emoji } from './Emoji';
import { cn, TEAM_COLOR, type Side } from './util';

export interface BudgetBarProps {
  points: number;
  max?: number;
  team: Side;
  /** Show the big "💰 11 / 20" counter above the bar. */
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Mirror the bar (fills from the right) — use for the right-hand corner. */
  mirrored?: boolean;
  className?: string;
}

const H = { sm: 'h-3', md: 'h-5', lg: 'h-7' } as const;

/**
 * Fighting-game style budget bar: when points drop, the fill snaps down while a yellow "damage"
 * chunk lingers and drains after a beat. Both fills are full-width layers moved with `translateX`
 * (transform only — no layout work per frame).
 */
export function BudgetBar({
  points,
  max = 20,
  team,
  showLabel = true,
  size = 'md',
  mirrored,
  className,
}: BudgetBarProps) {
  const reduce = useReducedMotion();
  const pct = Math.max(0, Math.min(100, (points / max) * 100));
  const broke = points <= 0;
  // Slide the layer out of the track by the missing fraction (to the left, or right when mirrored).
  const x = `${(mirrored ? 1 : -1) * (100 - pct)}%`;
  return (
    <div className={cn('w-full', className)}>
      {showLabel && (
        <div
          className={cn(
            'mb-1 flex items-baseline gap-1.5',
            mirrored ? 'justify-end' : 'justify-start',
          )}
        >
          <Emoji emoji="💰" label="" size={size === 'lg' ? 30 : 22} className="self-center" />
          <span
            className={cn(
              'comic tabular-nums',
              size === 'lg' ? 'text-[42px]' : 'text-[30px]',
              broke && 'text-team-b',
            )}
          >
            <CountUp value={points} from={points} decimals={0} duration={0.6} />
          </span>
          <span className="text-[14px] font-black text-ink-soft">/ {max}</span>
          {broke && (
            <span className="comic ml-1 -rotate-6 border-2 border-ink bg-team-b px-1.5 text-[14px] text-white shadow-ink-xs">
              METELİKSİZ!
            </span>
          )}
        </div>
      )}
      <div
        role="meter"
        aria-label="Kalan puan"
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={points}
        className={cn(
          'relative overflow-hidden border-3 border-ink bg-white shadow-ink-xs',
          H[size],
        )}
      >
        <motion.div
          aria-hidden
          className="absolute inset-0 bg-yellow will-change-transform"
          initial={false}
          animate={{ x }}
          transition={reduce ? { duration: 0 } : { delay: 0.45, duration: 0.55, ease: 'easeIn' }}
        />
        <motion.div
          aria-hidden
          className="absolute inset-0 bg-[repeating-linear-gradient(-45deg,rgb(255_255_255/0.2)_0_5px,transparent_5px_10px)] will-change-transform"
          style={{ backgroundColor: TEAM_COLOR[team] }}
          initial={false}
          animate={{ x }}
          transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 32 }}
        />
        {/* point ticks: every point faint, every 5 points stronger */}
        <div aria-hidden className="pointer-events-none absolute inset-0 flex">
          {Array.from({ length: max }, (_, i) => (
            <span
              key={i}
              className={cn(
                'h-full flex-1',
                i > 0 && (i % 5 === 0 ? 'border-l-2 border-ink/45' : 'border-l border-ink/12'),
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
